// src/views/Clientes/ActualizacionLimiteDeCredito/FiltroLimite.jsx
import React, { useRef, useEffect, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";

const ESTADO_CLIENTE_ACTIVO_ID = 11;
const PAGE_SIZE = 300;
const USUARIO_PAGE_SIZE = 500;

// Debounce genérico
function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return v;
}

export default function FiltroLimite({
  initialFilters = {},
  onClose = () => {},
  onSubmit = () => {},
}) {
  const filtroRef = useRef(null);

  // =======================================================
  //   REHIDRATAR FILTROS
  // =======================================================
  const [clienteNombre, setClienteNombre] = useState("");
  const [clienteId, setClienteId] = useState(null);

  const [usuarioAprobacion, setUsuarioAprobacion] = useState("");
  const [usuarioSolicitud, setUsuarioSolicitud] = useState("");
  const [fechaSolicitud, setFechaSolicitud] = useState("");
  const [fechaAprobacion, setFechaAprobacion] = useState("");
  const [estado, setEstado] = useState("");

  // =======================================================
  // Autocomplete cliente
  // =======================================================
  const [cliQuery, setCliQuery] = useState("");
  const debouncedQuery = useDebounced(cliQuery, 300);

  const [cliOpen, setCliOpen] = useState(false);
  const [cliItems, setCliItems] = useState([]);
  const [cliPage, setCliPage] = useState(0);
  const [cliHasMore, setCliHasMore] = useState(false);
  const [cliLoading, setCliLoading] = useState(false);

  const cliBoxRef = useRef(null);

  // =======================================================
  // Autocomplete usuarios (Aprobación / Solicitud)
  // =======================================================
  // Usuario Aprobación
  const [usrAprOpen, setUsrAprOpen] = useState(false);
  const [usrAprItems, setUsrAprItems] = useState([]);
  const [usrAprPage, setUsrAprPage] = useState(0);
  const [usrAprHasMore, setUsrAprHasMore] = useState(false);
  const [usrAprLoading, setUsrAprLoading] = useState(false);
  const usrAprBoxRef = useRef(null);
  const debouncedUsrApr = useDebounced(usuarioAprobacion, 300);

  // Usuario Solicitud
  const [usrSolOpen, setUsrSolOpen] = useState(false);
  const [usrSolItems, setUsrSolItems] = useState([]);
  const [usrSolPage, setUsrSolPage] = useState(0);
  const [usrSolHasMore, setUsrSolHasMore] = useState(false);
  const [usrSolLoading, setUsrSolLoading] = useState(false);
  const usrSolBoxRef = useRef(null);
  const debouncedUsrSol = useDebounced(usuarioSolicitud, 300);

  // ==============================
  // Cerrar clic fuera / ESC
  // ==============================
  useEffect(() => {
    const outside = (e) => {
      if (filtroRef.current && !filtroRef.current.contains(e.target)) onClose();
    };
    const esc = (e) => e.key === "Escape" && onClose();

    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", esc);

    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", esc);
    };
  }, [onClose]);

  // Cerrar dropdowns de autocompletes al hacer click fuera
  useEffect(() => {
    const handler = (e) => {
      if (cliBoxRef.current && !cliBoxRef.current.contains(e.target)) {
        setCliOpen(false);
      }
      if (usrAprBoxRef.current && !usrAprBoxRef.current.contains(e.target)) {
        setUsrAprOpen(false);
      }
      if (usrSolBoxRef.current && !usrSolBoxRef.current.contains(e.target)) {
        setUsrSolOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ==============================
  // Rehidratación real
  // ==============================
  useEffect(() => {
    if (!initialFilters) return;

    // Cliente
    if (initialFilters.clienteId) {
      setClienteId(initialFilters.clienteId);
      setClienteNombre(initialFilters.clienteNombre || "");
    }

    // Usuarios (código directamente)
    setUsuarioSolicitud(initialFilters.codigoUsuarioSolicitud || "");
    setUsuarioAprobacion(initialFilters.codigoUsuarioAprobacion || "");

    // Fechas yyyy-MM-dd
    if (initialFilters.dataFechaSolicitud) {
      setFechaSolicitud(initialFilters.dataFechaSolicitud.substring(0, 10));
    }

    if (initialFilters.dataFechaAprobacion) {
      setFechaAprobacion(initialFilters.dataFechaAprobacion.substring(0, 10));
    }

    // Estado → texto UI
    if (initialFilters.estadoId === 2) setEstado("SOLICITADA");
    else if (initialFilters.estadoId === 3) setEstado("APROBADA");
    else if (initialFilters.estadoId === 4) setEstado("RECHAZADA");
    else setEstado("");
  }, [initialFilters]);

  // ==============================
  // Helpers fetch
  // ==============================
  // Clientes
  const fetchClientesPage = async ({ page = 0, append = false }) => {
    if (cliLoading) return;

    setCliLoading(true);

    const q = debouncedQuery.trim();
    let url;

    if (q === "") {
      const params = new URLSearchParams();
      params.set("page", page);
      params.set("size", PAGE_SIZE);
      params.set("estadoId", ESTADO_CLIENTE_ACTIVO_ID);
      url = `${apiRoutes.clientes.list}?${params.toString()}`;
    } else {
      url = apiRoutes.clientes.autocompleteUrl({
        q,
        page,
        size: PAGE_SIZE,
        tipo: "cliente",
        browse: false,
        estadoId: ESTADO_CLIENTE_ACTIVO_ID,
      });
    }

    try {
      const res = await apiFetchJson(url, true);
      const items = res?.items || res?.content || [];

      let hasMore = false;

      if (res?.hasMore != null) hasMore = res.hasMore;
      else if (res?.totalElements != null)
        hasMore = (page + 1) * PAGE_SIZE < res.totalElements;
      else if (res?.totalPages != null) hasMore = page + 1 < res.totalPages;
      else hasMore = items.length === PAGE_SIZE;

      setCliItems((prev) => (append ? [...prev, ...items] : items));
      setCliHasMore(hasMore);
      setCliPage(page);
    } catch {
      if (!append) {
        setCliItems([]);
        setCliHasMore(false);
      }
    } finally {
      setCliLoading(false);
    }
  };

  // Usuarios (común para aprobación / solicitud)
  const fetchUsuariosPage = async ({ q = "", page = 0 }) => {
    const qs = (q ?? "").trim();
    const params = new URLSearchParams();
    if (qs) params.set("q", qs);
    params.set("page", page);
    params.set("size", USUARIO_PAGE_SIZE);

    const url = `${apiRoutes.usuarios.autocomplete}?${params.toString()}`;
    const res = await apiFetchJson(url, true);

    const raw = res?.items || res?.content || [];
    const items = raw.map((x) => {
      const id = x.id;
      const codigo = (x.codigo ?? "").toString().trim();
      const nombre = (x.nombre ?? "").toString().trim();
      const label =
        codigo && nombre
          ? `${codigo} — ${nombre}`
          : codigo || nombre || String(id);
      return { id, codigo, nombre, label };
    });

    let hasMore = false;
    if (res?.hasMore != null) hasMore = res.hasMore;
    else if (res?.totalElements != null)
      hasMore = (page + 1) * USUARIO_PAGE_SIZE < res.totalElements;
    else if (res?.totalPages != null) hasMore = page + 1 < res.totalPages;
    else hasMore = raw.length === USUARIO_PAGE_SIZE;

    return { items, hasMore, page };
  };

  // ==============================
  // Efectos de autocomplete
  // ==============================
  // Cliente
  useEffect(() => {
    if (!cliOpen) return;
    fetchClientesPage({ page: 0, append: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, cliOpen]);

  const handleScrollClientes = (e) => {
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;

    if (nearBottom && cliHasMore && !cliLoading) {
      fetchClientesPage({ page: cliPage + 1, append: true });
    }
  };

  const handleSelectCliente = (item) => {
    setClienteId(item.id);
    setClienteNombre(item.nombre ?? "");
    setCliOpen(false);
    setCliQuery("");
  };

  const handleClienteInputChange = (e) => {
    const val = e.target.value;
    setClienteNombre(val);
    setCliQuery(val);
    setClienteId(null);
    if (!cliOpen) setCliOpen(true);
  };

  const handleClienteFocus = () => {
    setCliOpen(true);
    if (cliItems.length === 0 && !cliLoading) {
      fetchClientesPage({ page: 0, append: false });
    }
  };

  // Usuario Aprobación
  useEffect(() => {
    if (!usrAprOpen) return;
    setUsrAprLoading(true);
    fetchUsuariosPage({ q: debouncedUsrApr, page: 0 })
      .then(({ items, hasMore, page }) => {
        setUsrAprItems(items);
        setUsrAprHasMore(hasMore);
        setUsrAprPage(page);
      })
      .catch((e) => {
        console.error("Autocomplete usuario aprobación:", e);
        setUsrAprItems([]);
        setUsrAprHasMore(false);
      })
      .finally(() => setUsrAprLoading(false));
  }, [usrAprOpen, debouncedUsrApr]);

  const onUsrAprScroll = (e) => {
    const el = e.currentTarget;
    if (!usrAprHasMore || usrAprLoading) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (!nearBottom) return;

    setUsrAprLoading(true);
    const next = usrAprPage + 1;
    fetchUsuariosPage({ q: debouncedUsrApr, page: next })
      .then(({ items, hasMore, page }) => {
        setUsrAprItems((prev) => [...prev, ...items]);
        setUsrAprHasMore(hasMore);
        setUsrAprPage(page);
      })
      .catch((e) => {
        console.error("Autocomplete usuario aprobación (scroll):", e);
      })
      .finally(() => setUsrAprLoading(false));
  };

  // Usuario Solicitud
  useEffect(() => {
    if (!usrSolOpen) return;
    setUsrSolLoading(true);
    fetchUsuariosPage({ q: debouncedUsrSol, page: 0 })
      .then(({ items, hasMore, page }) => {
        setUsrSolItems(items);
        setUsrSolHasMore(hasMore);
        setUsrSolPage(page);
      })
      .catch((e) => {
        console.error("Autocomplete usuario solicitud:", e);
        setUsrSolItems([]);
        setUsrSolHasMore(false);
      })
      .finally(() => setUsrSolLoading(false));
  }, [usrSolOpen, debouncedUsrSol]);

  const onUsrSolScroll = (e) => {
    const el = e.currentTarget;
    if (!usrSolHasMore || usrSolLoading) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (!nearBottom) return;

    setUsrSolLoading(true);
    const next = usrSolPage + 1;
    fetchUsuariosPage({ q: debouncedUsrSol, page: next })
      .then(({ items, hasMore, page }) => {
        setUsrSolItems((prev) => [...prev, ...items]);
        setUsrSolHasMore(hasMore);
        setUsrSolPage(page);
      })
      .catch((e) => {
        console.error("Autocomplete usuario solicitud (scroll):", e);
      })
      .finally(() => setUsrSolLoading(false));
  };

  // ==============================
  // SUBMIT
  // ==============================
  const handleSubmit = (e) => {
    e.preventDefault();

    const filtros = {
      clienteId: clienteId || null,
      clienteNombre: clienteNombre || null,

      // estos dos van como CÓDIGO (lo que quedó en el input)
      usuarioSolicitud: usuarioSolicitud || null,
      usuarioAprobacion: usuarioAprobacion || null,

      fechaSolicitud: fechaSolicitud || null,
      fechaAprobacion: fechaAprobacion || null,

      estado: estado || null,
    };

    onSubmit([], filtros);
  };

  return (
    <div className="drawer-backdrop">
      <div
        ref={filtroRef}
        onClick={(e) => e.stopPropagation()}
        className="drawer-panel overflow-auto"
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-title">Filtros</h2>
          <button onClick={onClose} className="btn-cancel" title="Cerrar">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4">
          {/* ======================
                Autocomplete Cliente
             ====================== */}
          <div>
            <label className="form-label-fil">Cliente</label>

            <div className="relative" ref={cliBoxRef}>
              <input
                type="text"
                className="input w-full"
                value={clienteNombre}
                onFocus={handleClienteFocus}
                onChange={handleClienteInputChange}
                placeholder="Buscar cliente..."
              />
              <input type="hidden" value={clienteId ?? ""} />

              {cliOpen && (
                <div
                  className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-64 overflow-auto text-sm"
                  onScroll={handleScrollClientes}
                >
                  {cliLoading && cliItems.length === 0 && (
                    <div className="px-3 py-2 text-gray-500">
                      Cargando clientes...
                    </div>
                  )}

                  {!cliLoading && cliItems.length === 0 && (
                    <div className="px-3 py-2 text-gray-500">
                      Sin resultados.
                    </div>
                  )}

                  {cliItems.map((it) => (
                    <button
                      key={it.id}
                      type="button"
                      className="block w-full text-left px-3 py-2 hover:bg-emerald-50"
                      onClick={() => handleSelectCliente(it)}
                    >
                      {it.codigo && (
                        <div className="font-mono text-xs text-gray-700">
                          {it.codigo}
                        </div>
                      )}
                      <div className="text-sm font-medium">{it.nombre}</div>
                      {it.documento && (
                        <div className="text-xs text-gray-500">
                          Doc: {it.documento}
                        </div>
                      )}
                    </button>
                  ))}

                  {cliLoading && cliItems.length > 0 && (
                    <div className="px-3 py-2 text-gray-400 text-xs">
                      Cargando más…
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Usuario Solicitud (autocomplete usuario) */}
          <div ref={usrSolBoxRef} className="relative">
            <label className="form-label-fil">Usuario Solicitud</label>
            <input
              type="text"
              className="input"
              placeholder="Código o nombre..."
              value={usuarioSolicitud}
              onChange={(e) => {
                setUsuarioSolicitud(e.target.value);
                setUsrSolOpen(true);
              }}
              onFocus={() => setUsrSolOpen(true)}
            />
            {usrSolOpen && (
              <div
                className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-64 overflow-auto text-sm"
                onScroll={onUsrSolScroll}
              >
                {usrSolItems.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    className="block w-full text-left px-3 py-2 hover:bg-emerald-50"
                    onClick={() => {
                      // en el input queda SOLO el código
                      setUsuarioSolicitud(u.codigo || "");
                      setUsrSolOpen(false);
                    }}
                  >
                    {u.label}
                  </button>
                ))}
                {usrSolLoading && (
                  <div className="px-3 py-2 text-gray-500">Cargando…</div>
                )}
                {!usrSolLoading && usrSolItems.length === 0 && (
                  <div className="px-3 py-2 text-gray-500">Sin resultados</div>
                )}
              </div>
            )}
          </div>

          {/* Usuario Aprobación (autocomplete usuario) */}
          <div ref={usrAprBoxRef} className="relative">
            <label className="form-label-fil">Usuario Aprobación</label>
            <input
              type="text"
              className="input"
              placeholder="Código o nombre..."
              value={usuarioAprobacion}
              onChange={(e) => {
                setUsuarioAprobacion(e.target.value);
                setUsrAprOpen(true);
              }}
              onFocus={() => setUsrAprOpen(true)}
            />
            {usrAprOpen && (
              <div
                className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-64 overflow-auto text-sm"
                onScroll={onUsrAprScroll}
              >
                {usrAprItems.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    className="block w-full text-left px-3 py-2 hover:bg-emerald-50"
                    onClick={() => {
                      // en el input queda SOLO el código
                      setUsuarioAprobacion(u.codigo || "");
                      setUsrAprOpen(false);
                    }}
                  >
                    {u.label}
                  </button>
                ))}
                {usrAprLoading && (
                  <div className="px-3 py-2 text-gray-500">Cargando…</div>
                )}
                {!usrAprLoading && usrAprItems.length === 0 && (
                  <div className="px-3 py-2 text-gray-500">Sin resultados</div>
                )}
              </div>
            )}
          </div>
          {/* Fecha Solicitud */}
          <div>
            <label className="form-label-fil">Fecha Solicitud</label>
            <input
              type="date"
              className="input"
              value={fechaSolicitud}
              onChange={(e) => setFechaSolicitud(e.target.value)}
            />
          </div>

          {/* Fecha Aprobación */}
          <div>
            <label className="form-label-fil">Fecha Aprobación</label>
            <input
              type="date"
              className="input"
              value={fechaAprobacion}
              onChange={(e) => setFechaAprobacion(e.target.value)}
            />
          </div>

          {/* Estado */}
          <div>
            <label className="form-label-fil">Estado</label>
            <select
              className="input"
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
            >
              <option value="">-- Seleccione --</option>
              <option value="SOLICITADA">Pendiente</option>
              <option value="APROBADA">Aprobada</option>
              <option value="RECHAZADA">Rechazada</option>
            </select>
          </div>

          {/* Botón aplicar */}
          <div className="text-right">
            <button type="submit" className="btn">
              Aplicar filtros
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
